import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle2, BookOpen, ChevronDown, ChevronUp, Lock, Lightbulb, ClipboardCheck, Trophy, Sparkles, XCircle, AlertCircle, Square, CheckSquare, GripVertical, Clock, AlertTriangle, ListChecks } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ScenarioBox, ChoiceOptions, ShortAnswerInput, ShortAnswerReview, isAnswered } from '../components/QuestionInputs';

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 0, opacity: isDragging ? 0.8 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className={`flex items-center gap-3 p-3 rounded-xl border-2 bg-white text-sm font-medium cursor-grab active:cursor-grabbing transition-shadow ${isDragging ? 'shadow-lg border-[#1B2A4A]' : 'border-stone-200 hover:border-stone-300'}`}>
      <GripVertical className="w-4 h-4 text-stone-400 shrink-0" />
      <span className="flex-1">{children}</span>
    </div>
  );
}

// ============ SINGLE MODULE VIEWER ============
function SingleModuleViewer({ module, api, navigate, moduleId }) {
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(module.user_progress?.completed_lessons || []);
  const [expandedPoints, setExpandedPoints] = useState(true);

  const markComplete = async (lessonIndex) => {
    try {
      await api('post', `/training-modules/${moduleId}/progress`, { lesson_index: lessonIndex });
      setCompletedLessons(prev => [...new Set([...prev, lessonIndex])]);
      toast.success('Lesson completed!');
      if (lessonIndex < (module?.lessons?.length || 0) - 1) setCurrentLesson(lessonIndex + 1);
    } catch { toast.error('Failed to update progress'); }
  };

  const lessons = module.lessons || [];
  const lesson = lessons[currentLesson];
  const progress = lessons.length > 0 ? Math.round((completedLessons.length / lessons.length) * 100) : 0;
  const isCurrentCompleted = completedLessons.includes(currentLesson);

  return (
    <>
      <div className="flex items-center gap-3 mt-3 mb-6">
        <Progress value={progress} className="h-2 flex-1 bg-stone-100" data-testid="module-progress-bar" />
        <span className="text-sm font-medium text-stone-600">{progress}%</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide" data-testid="lesson-navigation">
        {lessons.map((l, i) => (
          <button key={i} onClick={() => setCurrentLesson(i)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all shrink-0 ${i === currentLesson ? 'bg-[#1B2A4A] text-white shadow-sm' : completedLessons.includes(i) ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
            data-testid={`lesson-nav-${i}`}>
            {completedLessons.includes(i) && <CheckCircle2 className="w-3.5 h-3.5" />}
            <span>Lesson {i + 1}</span>
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        {lesson && (
          <motion.div key={currentLesson} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4" data-testid="lesson-content">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#D9480F]" />
                    <h2 className="text-lg sm:text-xl font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{lesson.title}</h2></div>
                  {isCurrentCompleted && <Badge className="bg-green-100 text-green-700">Completed</Badge>}
                </div>
                <div className="prose prose-stone max-w-none"><p className="text-stone-600 leading-relaxed whitespace-pre-line" style={{ fontFamily: "'Manrope', sans-serif" }}>{lesson.content}</p></div>
                {lesson.images?.length > 0 && (
                  <div className="mt-4 space-y-3">{lesson.images.map((img) => {
                    const imgUrl = typeof img === 'string' ? img : img.url;
                    const caption = typeof img === 'string' ? '' : (img.caption || '');
                    return (<figure key={imgUrl} className="rounded-xl overflow-hidden border border-stone-200"><img src={imgUrl} alt={caption || 'Lesson material'} className="w-full h-auto object-contain max-h-64" loading="lazy" />{caption && <figcaption className="px-3 py-2 text-xs text-stone-500 italic bg-stone-50 border-t border-stone-100">{caption}</figcaption>}</figure>);
                  })}</div>
                )}
                {lesson.key_points?.length > 0 && (
                  <div className="mt-6">
                    <button onClick={() => setExpandedPoints(!expandedPoints)} className="flex items-center gap-2 text-sm font-semibold text-stone-700 mb-2" data-testid="toggle-key-points">
                      Key Takeaways {expandedPoints ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {expandedPoints && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="space-y-2">
                        {lesson.key_points.map((point, i) => (
                          <div key={i} className="flex items-start gap-2 p-3 rounded-xl bg-[#FFF4E6] border border-orange-100">
                            <div className="w-5 h-5 rounded-full bg-[#D9480F] text-white text-xs flex items-center justify-center shrink-0 mt-0.5">{i + 1}</div>
                            <p className="text-sm text-stone-700">{point}</p>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="flex items-center justify-between gap-3">
              <Button variant="outline" onClick={() => setCurrentLesson(Math.max(0, currentLesson - 1))} disabled={currentLesson === 0} className="rounded-xl border-stone-200" data-testid="prev-lesson-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Previous</Button>
              <div className="flex gap-2">
                {!isCurrentCompleted && <Button onClick={() => markComplete(currentLesson)} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="complete-lesson-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Mark Complete</Button>}
                {currentLesson < lessons.length - 1 ? (
                  <Button variant="outline" onClick={() => setCurrentLesson(currentLesson + 1)} className="rounded-xl border-stone-200" data-testid="next-lesson-btn">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
                ) : progress === 100 && (
                  <Button onClick={() => navigate(`/certificate/${moduleId}`)} className="bg-green-600 text-white rounded-xl hover:bg-green-700" data-testid="get-certificate-btn">Get Certificate</Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ============ SECTION QUIZ COMPONENT ============
function SectionQuiz({ quiz, onSubmit, submitting }) {
  const [answers, setAnswers] = useState({});
  const [orderStates, setOrderStates] = useState({});
  const questions = quiz?.questions || [];
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const answeredCount = questions.filter((q, i) => isAnswered(q, answers[i])).length;

  // Initialize ordering answers
  useEffect(() => {
    const inits = {};
    questions.forEach((q, i) => {
      if (q.type === 'ordering' && !orderStates[i]) {
        const co = Array.isArray(q.correct_order) ? q.correct_order : [];
        inits[i] = [...co].sort(() => Math.random() - 0.5);
      }
    });
    if (Object.keys(inits).length > 0) {
      setOrderStates(prev => ({ ...prev, ...inits }));
      setAnswers(prev => ({ ...prev, ...Object.fromEntries(Object.entries(inits).map(([k, v]) => [k, v])) }));
    }
  }, [questions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (answeredCount < questions.length) { toast.error('Please answer all questions'); return; }
    onSubmit(answers);
  };

  return (
    <div className="space-y-4" data-testid="section-quiz">
      <div className="flex items-center gap-2 mb-2">
        <ClipboardCheck className="w-5 h-5 text-[#D9480F]" />
        <h3 className="text-base font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Section Quiz</h3>
        <span className="text-xs text-stone-400 ml-auto">{answeredCount}/{questions.length} answered</span>
      </div>
      {questions.map((q, qIdx) => (
        <Card key={qIdx} className="rounded-xl border border-stone-200" data-testid={`quiz-question-${qIdx}`}>
          <CardContent className="p-4">
            {q.type === 'scenario' && <ScenarioBox scenario={q.scenario} compact />}
            <p className="text-sm font-semibold text-stone-900 mb-3">
              <span className="text-stone-400 mr-1">Q{qIdx + 1}.</span> {q.question}
            </p>
            {q.type === 'short_answer' ? (
              <ShortAnswerInput value={answers[qIdx]} onChange={(v) => setAnswers(prev => ({ ...prev, [qIdx]: v }))} questionIdx={qIdx} />
            ) : q.type === 'scenario' ? (
              <ChoiceOptions options={q.options} value={answers[qIdx]} onChange={(idx) => setAnswers(prev => ({ ...prev, [qIdx]: idx }))} questionIdx={qIdx} compact />
            ) : q.type === 'ordering' ? (
              <div>
                <p className="text-xs text-stone-400 mb-2">Drag to reorder</p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => {
                  const { active, over } = event;
                  if (active.id !== over?.id) {
                    const items = orderStates[qIdx] || [];
                    const ids = items.map((_, i) => `sq-${qIdx}-${i}`);
                    const oldIdx = ids.indexOf(active.id);
                    const newIdx = ids.indexOf(over.id);
                    const newOrder = arrayMove(items, oldIdx, newIdx);
                    setOrderStates(prev => ({ ...prev, [qIdx]: newOrder }));
                    setAnswers(prev => ({ ...prev, [qIdx]: newOrder }));
                  }
                }}>
                  <SortableContext items={(orderStates[qIdx] || []).map((_, i) => `sq-${qIdx}-${i}`)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1.5">
                      {(orderStates[qIdx] || []).map((item, i) => (
                        <SortableItem key={`sq-${qIdx}-${i}`} id={`sq-${qIdx}-${i}`}>
                          <span className="text-stone-400 text-xs mr-2">{i + 1}.</span>{item}
                        </SortableItem>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            ) : q.type === 'multi_select' ? (
              <div className="space-y-2">
                <p className="text-xs text-stone-400">Select all that apply</p>
                {q.options?.map((opt, optIdx) => {
                  const selected = answers[qIdx] || [];
                  const isSelected = Array.isArray(selected) && selected.includes(optIdx);
                  const noneIdx = q.options.findIndex(o => o.toLowerCase() === 'none of the above');
                  return (
                    <button key={optIdx} onClick={() => {
                      let newSel;
                      if (optIdx === noneIdx && noneIdx >= 0) { newSel = isSelected ? [] : [optIdx]; }
                      else { newSel = (selected || []).filter(i => i !== noneIdx); newSel = isSelected ? newSel.filter(i => i !== optIdx) : [...newSel, optIdx]; }
                      setAnswers(prev => ({ ...prev, [qIdx]: newSel }));
                    }} className={`w-full text-left p-3 rounded-lg border text-sm ${isSelected ? 'border-[#1B2A4A] bg-[#1B2A4A]/5' : 'border-stone-200 hover:border-stone-300'}`}>
                      <div className="flex items-center gap-2">
                        {isSelected ? <CheckSquare className="w-4 h-4 text-[#1B2A4A] shrink-0" /> : <Square className="w-4 h-4 text-stone-300 shrink-0" />}
                        {opt}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {q.options?.map((opt, optIdx) => (
                  <button key={optIdx} onClick={() => setAnswers(prev => ({ ...prev, [qIdx]: optIdx }))}
                    className={`w-full text-left p-3 rounded-lg border text-sm ${answers[qIdx] === optIdx ? 'border-[#1B2A4A] bg-[#1B2A4A]/5' : 'border-stone-200 hover:border-stone-300'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${answers[qIdx] === optIdx ? 'border-[#1B2A4A] bg-[#1B2A4A]' : 'border-stone-300'}`}>
                        {answers[qIdx] === optIdx && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      {opt}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      <Button onClick={handleSubmit} disabled={submitting || answeredCount < questions.length}
        className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="submit-section-quiz-btn">
        {submitting ? 'Submitting...' : 'Submit Quiz'}
      </Button>
      {answeredCount < questions.length && (
        <p className="text-xs text-amber-600 text-center flex items-center justify-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Answer all {questions.length} questions to submit</p>
      )}
    </div>
  );
}

// ============ MODULAR MODULE VIEWER ============
function ModularViewer({ module, api, navigate, moduleId, reload }) {
  const [activeSection, setActiveSection] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const sections = module.lessons || [];
  const sp = module.section_progress || { completed: 0, total: sections.length, percentage: 0 };

  const submitQuiz = async (answers) => {
    setSubmitting(true);
    try {
      const { data } = await api('post', `/training-modules/${moduleId}/sections/${activeSection}/submit-quiz`, { answers });
      setQuizResult(data);
      if (data.passed) {
        toast.success(`Section quiz passed! Score: ${data.score}%`);
        if (data.all_sections_complete) {
          const colors = ['#1B2A4A', '#D9480F', '#F59E0B', '#10B981'];
          confetti({ particleCount: 100, spread: 100, origin: { y: 0.6 }, colors });
        }
      } else {
        toast.error(`Score: ${data.score}%. Need ${data.passing_score}% to pass.`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit quiz'); }
    finally { setSubmitting(false); }
  };

  const closeQuizResult = () => {
    setQuizResult(null);
    setActiveSection(null);
    reload();
  };

  // If viewing a quiz result
  if (quizResult) {
    return (
      <div data-testid="section-quiz-result">
        <Card className={`rounded-2xl border-2 mb-4 ${quizResult.passed ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
          <CardContent className="p-6 text-center">
            {quizResult.passed ? <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-2" /> : <XCircle className="w-14 h-14 text-red-500 mx-auto mb-2" />}
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: quizResult.passed ? '#16A34A' : '#DC2626' }}>
              {quizResult.passed ? 'Section Passed!' : 'Not Quite'}
            </h2>
            <p className="text-4xl font-bold text-stone-900 mb-1">{quizResult.score}%</p>
            <p className="text-stone-600 text-sm">{quizResult.correct}/{quizResult.total} correct — Passing: {quizResult.passing_score}%</p>
            {quizResult.next_section_unlocked && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="inline-flex items-center gap-2 bg-[#1B2A4A] text-white px-4 py-2 rounded-full text-sm font-medium mt-4">
                <Sparkles className="w-4 h-4" /> Next section unlocked!
              </motion.div>
            )}
            {quizResult.all_sections_complete && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="flex flex-col items-center justify-center gap-2 mt-4">
                <div className="flex items-center gap-2"><Trophy className="w-6 h-6 text-[#D9480F]" /><span className="text-lg font-bold text-[#1B2A4A]">All Sections Complete!</span></div>
                {quizResult.signoff_pending && (
                  <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full text-xs font-medium" data-testid="modular-signoff-pending"><Clock className="w-3.5 h-3.5" /> Sent to your manager for sign-off</span>
                )}
              </motion.div>
            )}
            <div className="flex gap-3 justify-center mt-5">
              <Button onClick={closeQuizResult} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]">Continue</Button>
            </div>
          </CardContent>
        </Card>
        {(quizResult.review || []).filter(r => r.type === 'short_answer' || !r.is_correct).length > 0 && (
          <div className="space-y-3" data-testid="section-quiz-review">
            <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: "'Outfit', sans-serif" }}>Feedback</h3>
            {(quizResult.review || []).filter(r => r.type === 'short_answer' || !r.is_correct).map(item => (
              <Card key={item.index} className={`rounded-xl border ${item.is_correct ? 'border-green-200' : 'border-red-200'}`}>
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-stone-900 mb-2"><span className="text-stone-400 mr-1">Q{item.index + 1}.</span>{item.question}</p>
                  {item.type === 'short_answer' ? <ShortAnswerReview item={item} /> : (
                    <div className="ml-7 text-sm space-y-1">
                      {item.type === 'ordering' ? <p className="text-stone-600">Correct order: {(item.correct_order || []).join(' → ')}</p>
                        : item.type === 'multi_select' ? <p className="text-stone-600">Correct: {(item.correct_answers || []).map(i => item.options?.[i]).join(', ')}</p>
                        : <p className="text-stone-600">Correct answer: <span className="font-medium text-green-700">{item.options?.[item.correct_answer]}</span></p>}
                      {item.explanation && <p className="text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2">Why: {item.explanation}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // If viewing a section
  if (activeSection !== null && sections[activeSection]) {
    const sec = sections[activeSection];
    return (
      <div data-testid={`section-viewer-${activeSection}`}>
        <Button variant="ghost" onClick={() => setActiveSection(null)} className="text-stone-600 hover:text-stone-900 -ml-2 mb-4 rounded-xl">
          <ArrowLeft className="w-4 h-4 mr-1" /> All Sections
        </Button>
        <Badge className="bg-[#1B2A4A]/10 text-[#1B2A4A] mb-2">Section {activeSection + 1} of {sections.length}</Badge>
        <h2 className="text-xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{sec.title}</h2>

        {/* Key Takeaways */}
        {sec.key_takeaways && (
          <Card className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white mb-4" data-testid="key-takeaways">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-semibold text-amber-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Key Takeaways</h3>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">{sec.key_takeaways}</p>
            </CardContent>
          </Card>
        )}

        {/* Content */}
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4">
          <CardContent className="p-5 sm:p-6">
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-600 leading-relaxed whitespace-pre-line" style={{ fontFamily: "'Manrope', sans-serif" }}>{sec.content}</p>
            </div>
            {sec.key_points?.length > 0 && (
              <div className="mt-6 space-y-2">
                <h4 className="text-sm font-semibold text-stone-700 mb-2">Key Points</h4>
                {sec.key_points.map((point, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 rounded-xl bg-[#FFF4E6] border border-orange-100">
                    <div className="w-5 h-5 rounded-full bg-[#D9480F] text-white text-xs flex items-center justify-center shrink-0 mt-0.5">{i + 1}</div>
                    <p className="text-sm text-stone-700">{point}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section Quiz */}
        {sec.quiz?.questions?.length > 0 && !sec.quiz_passed && (
          <SectionQuiz quiz={sec.quiz} onSubmit={submitQuiz} submitting={submitting} />
        )}
        {sec.quiz_passed && (
          <Card className="rounded-2xl border border-green-200 bg-green-50 mb-4">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-green-800 font-semibold">Quiz Passed — Score: {sec.quiz_score}%</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Section overview
  return (
    <div data-testid="modular-overview">
      <div className="flex items-center gap-3 mt-3 mb-4">
        <Progress value={sp.percentage} className="h-2.5 flex-1 bg-stone-100" data-testid="modular-progress-bar" />
        <span className="text-sm font-medium text-stone-600">{sp.completed}/{sp.total} sections</span>
      </div>
      <div className="space-y-3">
        {sections.map((sec, i) => {
          const isLocked = !sec.unlocked;
          const isPassed = sec.quiz_passed;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className={`rounded-2xl border-2 transition-all ${isLocked ? 'border-stone-200 bg-stone-50 opacity-60' : isPassed ? 'border-green-200 bg-green-50/30 hover:shadow-md cursor-pointer' : 'border-stone-200 bg-white hover:shadow-md hover:border-stone-300 cursor-pointer'}`}
                onClick={() => !isLocked && setActiveSection(i)} data-testid={`section-card-${i}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isPassed ? 'bg-green-500 text-white' : isLocked ? 'bg-stone-200 text-stone-400' : 'bg-[#1B2A4A] text-white'}`}>
                          {isPassed ? <CheckCircle2 className="w-4 h-4" /> : isLocked ? <Lock className="w-3.5 h-3.5" /> : i + 1}
                        </div>
                        <h3 className={`font-semibold text-base ${isLocked ? 'text-stone-400' : 'text-stone-900'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {sec.title}
                        </h3>
                        {isPassed && <Badge className="bg-green-100 text-green-700 text-xs">Passed</Badge>}
                        {sec.quiz_score !== null && sec.quiz_score !== undefined && !isPassed && (
                          <Badge className="bg-red-100 text-red-700 text-xs">Score: {sec.quiz_score}%</Badge>
                        )}
                      </div>
                      {sec.key_takeaways && (
                        <p className={`text-sm line-clamp-2 mt-1 ${isLocked ? 'text-stone-300' : 'text-stone-500'}`}>{sec.key_takeaways}</p>
                      )}
                      {isLocked && <p className="text-xs text-amber-600 mt-2 font-medium">Complete Section {i}'s quiz to unlock</p>}
                      {!isLocked && sec.quiz?.questions?.length > 0 && (
                        <p className="text-xs text-stone-400 mt-1">{sec.quiz.questions.length} quiz questions</p>
                      )}
                    </div>
                    {!isLocked && <ArrowRight className="w-5 h-5 text-stone-400 shrink-0 mt-1" />}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
      {sp.percentage >= 100 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 text-center">
          <Button onClick={() => navigate(`/certificate/${moduleId}`)} className="bg-green-600 text-white rounded-xl hover:bg-green-700" data-testid="get-certificate-btn">
            <Trophy className="w-4 h-4 mr-2" /> Get Certificate
          </Button>
        </motion.div>
      )}
    </div>
  );
}

// ============ MAIN COMPONENT ============
export default function TrainingViewer() {
  const { moduleId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);

  const loadModule = useCallback(async () => {
    try {
      const { data } = await api('get', `/training-modules/${moduleId}`);
      setModule(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load training module');
      navigate('/');
    }
  }, [api, moduleId, navigate]);

  useEffect(() => { loadModule(); }, [loadModule]);

  if (!module) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-[#D9480F] border-t-transparent rounded-full" />
      </div>
    );
  }

  const signoff = module.signoff;
  const inPath = !!module.path_status;
  const quizPassed = module.module_type === 'modular' ? module.section_progress?.percentage >= 100 : !!signoff;

  return (
    <div className="pb-20 md:pb-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-2">
        <Button variant="ghost" onClick={() => navigate('/')} className="text-stone-600 hover:text-stone-900 mb-3 -ml-2 rounded-xl" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{module.title}</h1>
          {module.module_type === 'modular' && <Badge className="bg-blue-100 text-blue-700 text-xs">Modular</Badge>}
        </div>
        <p className="text-sm text-stone-500">{module.description}</p>
      </motion.div>

      {inPath && signoff?.status === 'pending_signoff' && (
        <Card className="rounded-2xl border border-amber-200 bg-amber-50 mb-4" data-testid="signoff-banner-pending">
          <CardContent className="p-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div><p className="text-sm font-semibold text-amber-900">Awaiting manager sign-off</p><p className="text-xs text-amber-800 mt-0.5">You passed the quiz with {signoff.quiz_score}%. Your next module unlocks once your manager digitally signs off.</p></div>
          </CardContent>
        </Card>
      )}
      {inPath && signoff?.status === 'rejected' && (
        <Card className="rounded-2xl border border-red-200 bg-red-50 mb-4" data-testid="signoff-banner-rejected">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div><p className="text-sm font-semibold text-red-900">Sent back by {signoff.reviewed_by_name}</p><p className="text-xs text-red-800 mt-0.5">"{signoff.review_note}"</p><p className="text-xs text-red-700 mt-1">Review the material and retake the quiz to resubmit for sign-off.</p></div>
          </CardContent>
        </Card>
      )}
      {inPath && signoff?.status === 'approved' && (
        <Card className="rounded-2xl border border-green-200 bg-green-50 mb-4" data-testid="signoff-banner-approved">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div><p className="text-sm font-semibold text-green-900">Signed off by {signoff.reviewed_by_name}</p><p className="text-xs text-green-800 mt-0.5">{new Date(signoff.reviewed_at).toLocaleString()} · Signature: <span className="italic font-medium">{signoff.signature_name}</span>. You can revisit this module anytime.</p></div>
          </CardContent>
        </Card>
      )}

      {module.practical_checklist?.length > 0 && (
        <Card className="rounded-2xl border border-stone-200 bg-white mb-4" data-testid="practical-checklist-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><ListChecks className="w-4 h-4 text-[#D9480F]" /><p className="text-sm font-semibold text-stone-900">Hands-on skills your manager will verify</p></div>
            <ul className="space-y-1.5">
              {module.practical_checklist.map((c, i) => {
                const verified = signoff?.status === 'approved';
                return (
                  <li key={c.id || i} className="flex items-start gap-2 text-sm text-stone-700">
                    {verified ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" /> : <div className="w-4 h-4 rounded border border-stone-300 shrink-0 mt-0.5" />}
                    <span className={verified ? 'text-stone-500' : ''}>{c.text}</span>
                  </li>
                );
              })}
            </ul>
            {signoff?.status !== 'approved' && <p className="text-[11px] text-stone-400 mt-2">Your manager must physically watch you demonstrate these during sign-off.</p>}
          </CardContent>
        </Card>
      )}

      {module.module_type !== 'modular' && module.test_id && (
        <Card className="rounded-2xl border border-stone-200 bg-white mb-4" data-testid="module-quiz-card">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B2A4A]/10 text-[#1B2A4A] flex items-center justify-center"><ClipboardCheck className="w-5 h-5" /></div>
              <div><p className="text-sm font-semibold text-stone-900">Module Quiz</p><p className="text-xs text-stone-500">{quizPassed ? 'Passed — retake anytime for practice' : 'Pass the quiz to complete this module'}</p></div>
            </div>
            <Button onClick={() => navigate(`/test/${module.test_id}`)} className={`rounded-xl text-sm ${signoff?.status === 'rejected' || !quizPassed ? 'bg-[#D9480F] hover:bg-[#c2410c] text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} data-testid="take-quiz-btn">
              {signoff?.status === 'rejected' ? 'Retake Quiz' : quizPassed ? 'Review Quiz' : 'Take Quiz'}
            </Button>
          </CardContent>
        </Card>
      )}

      {module.module_type === 'modular' ? (
        <ModularViewer module={module} api={api} navigate={navigate} moduleId={moduleId} reload={loadModule} />
      ) : (
        <SingleModuleViewer module={module} api={api} navigate={navigate} moduleId={moduleId} />
      )}
    </div>
  );
}
